import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router-dom';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import CreateUserModal from '@/components/admin/users/CreateUserModal.tsx';
import FullscreenError from '@/components/utils/FullscreenError';
import FeatureIcons from '@/components/admin/common/FeatureIcons';
import {
  faUsers,
  faSpinner,
  faArrowLeft,
  faPlus,
  faUserGroup,
  faSearch,
  faXmark,
  faDownload,
  faStarfighterTwinIonEngine
} from '@/fontawesome';
import { handleAPIError } from '@/utils/toasts';
import { useUser } from '@/contexts/UserContext';
import { formatDate, formatRelativeTime, shortObjectID } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import {
  type User,
  type PermissionGroup,
  type FeatureType,
  ALL_PERMISSION_GROUPS,
  PERMISSION_GROUP_LABELS, FEATURE_TYPE_LABELS, ALL_FEATURE_TYPES, type SubscriptionType,
  type SubscriptionProductType, isEmptyFeatureConfig
} from '@/models';
import SubscriptionProductTypeIcons from '@/components/admin/common/SubscriptionProductTypeIcons';
import { useSubscriptionProducts } from '@/contexts/SubscriptionProductsContext';

const pageSize = 50;

const showSubscriptioTypeFilter = false;

export default function AdminUsersList() {
  const navigate = useNavigate();
  const { isAdmin } = useUser();
  const { products, productTypes, getTypeLabel } = useSubscriptionProducts();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [exporting, setExporting] = useState(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedPermissionGroups, setSelectedPermissionGroups] = useState<PermissionGroup[]>([]);
  const [selectedFeatureTypes, setSelectedFeatureTypes] = useState<FeatureType[]>([]);
  const [selectedSubscriptionTypes, setSelectedSubscriptionTypes] = useState<SubscriptionType[]>([]);
  const [selectedSubscriptionProductTypes, setSelectedSubscriptionProductTypes] = useState<SubscriptionProductType[]>([]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, selectedPermissionGroups, selectedFeatureTypes, selectedSubscriptionTypes, selectedSubscriptionProductTypes]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      // Generate cursor token for page-based navigation
      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;

      const response = await APIRoutes.listUsers({
        limit: pageSize,
        next: cursorToken,
        search: debouncedSearchTerm || undefined,
        permissionGroup: selectedPermissionGroups.length > 0 ? selectedPermissionGroups : undefined,
        featureType: selectedFeatureTypes.length > 0 ? selectedFeatureTypes : undefined,
        activeSubscriptionTypes: selectedSubscriptionTypes.length > 0 ? selectedSubscriptionTypes : undefined,
        activeSubscriptionProductTypes: selectedSubscriptionProductTypes.length > 0 ? selectedSubscriptionProductTypes : undefined,
      });
      setUsers(response.users);
      setTotal(response.total);
    } catch (err) {
      console.error('Critical error loading users list:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Benutzerliste');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearchTerm, selectedPermissionGroups, selectedFeatureTypes, selectedSubscriptionTypes, selectedSubscriptionProductTypes]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleUserCreated = () => {
    fetchUsers();
  };

  const togglePermissionGroup = (group: PermissionGroup) => {
    setSelectedPermissionGroups(prev =>
      prev.includes(group)
        ? prev.filter(g => g !== group)
        : [...prev, group]
    );
  };

  const toggleFeatureType = (feature: FeatureType) => {
    setSelectedFeatureTypes(prev =>
      prev.includes(feature)
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    );
  };

  const toggleSubscriptionType = (subscription: SubscriptionType) => {
    setSelectedSubscriptionTypes(prev =>
      prev.includes(subscription)
        ? prev.filter(f => f !== subscription)
        : [...prev, subscription]
    );
  };

  const toggleSubscriptionProductType = (subscription: SubscriptionProductType) => {
    setSelectedSubscriptionProductTypes(prev =>
      prev.includes(subscription)
        ? prev.filter(f => f !== subscription)
        : [...prev, subscription]
    );
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedPermissionGroups([]);
    setSelectedFeatureTypes([]);
    setSelectedSubscriptionTypes([]);
    setSelectedSubscriptionProductTypes([]);
  };

  const hasActiveFilters = searchTerm !== '' || selectedPermissionGroups.length > 0 || selectedFeatureTypes.length > 0 || selectedSubscriptionTypes.length > 0 || selectedSubscriptionProductTypes.length > 0;

  const handleExport = async () => {
    setExporting(true);
    try {
      await APIRoutes.exportUsers();
    } catch (err) {
      handleAPIError(err, 'Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Benutzerliste nicht verfügbar"
        message={error || 'Die Benutzerliste konnte nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchUsers}
        onBack={() => navigate('/admin')}
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to="/admin"
              className="mr-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </Link>
            <div className="flex items-center">
              <FontAwesomeIcon icon={faUsers} className="w-8 h-8 text-blue-600 dark:text-blue-400 mr-3" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Benutzerliste ({total})
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              disabled={!isAdmin || exporting}
              title={isAdmin ? 'Nutzer exportieren' : 'Nur für Administratoren'}
              className={`bg-green-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${!isAdmin || exporting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-700 cursor-pointer'}`}
            >
              <FontAwesomeIcon icon={exporting ? faSpinner : faDownload} className="w-4 h-4" spin={exporting} />
              <span>{exporting ? 'Exportiert...' : 'Export'}</span>
            </button>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              disabled={!isAdmin}
              title={isAdmin ? 'Neuen Nutzer erstellen' : 'Nur für Administratoren'}
              className={`bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700 cursor-pointer'}`}
            >
              <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
              <span>Neuer Nutzer erstellen</span>
            </button>
          </div>
        </div>

      {/* Filter Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col gap-4">
          {/* Search Bar */}
          <div className="flex-1">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              />
              <input
                type="text"
                placeholder="Suche nach Name oder E-Mail..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchTerm !== '' && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer"
                >
                  <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Permission Group Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Rolle
            </label>
            <div className="flex flex-wrap gap-2">
              {ALL_PERMISSION_GROUPS.map((group) => (
                <button
                  key={group}
                  onClick={() => togglePermissionGroup(group)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors cursor-pointer ${
                    selectedPermissionGroups.includes(group)
                      ? group === 'ADMIN'
                        ? 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/50 dark:text-red-400 dark:border-red-700'
                        : group === 'EMPLOYEE'
                        ? 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/50 dark:text-orange-400 dark:border-orange-700'
                        : 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/50 dark:text-green-400 dark:border-green-700'
                      : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                  }`}
                >
                  {PERMISSION_GROUP_LABELS[group]}
                </button>
              ))}
            </div>
          </div>

          {/* Feature Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Features
            </label>
            <div className="flex flex-wrap gap-2">
              {ALL_FEATURE_TYPES.map((feature) => (
                <button
                  key={feature}
                  onClick={() => toggleFeatureType(feature)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors cursor-pointer ${
                    selectedFeatureTypes.includes(feature)
                      ? 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/50 dark:text-blue-400 dark:border-blue-700'
                      : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                  }`}
                >
                  {FEATURE_TYPE_LABELS[feature]}
                </button>
              ))}
            </div>
          </div>

          {/* SubscriptionProductType Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Subscriptions
            </label>
            <div className="flex flex-wrap gap-2">
              {productTypes.map(({ productType, label }) => (
                <button
                  key={productType}
                  onClick={() => toggleSubscriptionProductType(productType)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors cursor-pointer ${
                    selectedSubscriptionProductTypes.includes(productType)
                      ? 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/50 dark:text-blue-400 dark:border-blue-700'
                      : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* SubscriptionType Filter */}
          { showSubscriptioTypeFilter && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Subscriptions
              </label>
              <div className="flex flex-wrap gap-2">
                {products.map((product) => (
                  <button
                    key={product.type}
                    onClick={() => toggleSubscriptionType(product.type)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors cursor-pointer ${
                      selectedSubscriptionTypes.includes(product.type)
                        ? 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/50 dark:text-blue-400 dark:border-blue-700'
                        : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                    }`}
                  >
                    {getTypeLabel(product.type)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <div className="flex justify-end">
              <button
                onClick={clearAllFilters}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline cursor-pointer"
              >
                Alle Filter zurücksetzen
              </button>
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {users.length > 0 && !loading && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Benutzer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Benutzer-ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Rolle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Features
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Subscriptions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Erstellt
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Zuletzt aktiv
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/admin/users/${user.id}`} className="block">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {user.email}
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/admin/users/${user.id}`} className="block">
                          <div className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                            {shortObjectID(user.id)}
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/admin/users/${user.id}`} className="block">
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.permissionGroup === 'ADMIN'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400'
                                : user.permissionGroup === 'EMPLOYEE'
                                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-400'
                                : 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
                            }`}>
                              {user.permissionGroup === 'ADMIN' ? 'Administrator' : user.permissionGroup === 'EMPLOYEE' ? 'Mitarbeiter' : 'Benutzer'}
                            </span>
                            {user.featureConfig.communityMember && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-400" title="Community-Mitglied">
                                <FontAwesomeIcon icon={faUserGroup} className="w-3 h-3" />
                              </span>
                            )}
                            {!isEmptyFeatureConfig(user.manualFeatureConfig) && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/50 dark:text-fuchsia-400" title="Feature-Config manuell überschrieben">
                                <FontAwesomeIcon icon={faStarfighterTwinIonEngine} className="w-3 h-3" />
                              </span>
                            )}
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/admin/users/${user.id}`} className="block">
                          <FeatureIcons featureConfig={user.featureConfig} />
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/admin/users/${user.id}`} className="block">
                          <SubscriptionProductTypeIcons productTypes={user.activeSubscriptionProductTypes} />
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <Link to={`/admin/users/${user.id}`} className="block">
                          {formatDate(user.created)}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <Link to={`/admin/users/${user.id}`} className="block">
                          {formatRelativeTime(user.lastActive)}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <PageBasedPagination
              currentPage={currentPage}
              totalCount={total}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              loading={loading}
            />
        </div>
      )}

      {users.length === 0 && !loading && !error && (
        <div className="text-center py-12">
          <FontAwesomeIcon icon={faUsers} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            {hasActiveFilters ? 'Keine Benutzer gefunden, die den Filterkriterien entsprechen' : 'Keine Benutzer gefunden'}
          </p>
        </div>
      )}

      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onUserCreated={handleUserCreated}
      />
    </>
  );
}
